import { useEffect, useState } from 'react';

import { PricingsAssignmentType } from 'api/src/lib/pricings';

import { useQuery } from '@redwoodjs/web';

import { GET_PARTNERS } from 'src/graphql/queries/partners.query';
import { GET_PARTNER_OF_PARTNERS_OF_USER } from 'src/graphql/queries/subpartners.query';

export const usePartnerAssignment = ({
    idToAssign,
    setIdToAssign,
    assignedPartners,
    assignedPartnersOfPartners,
    refetch,
    assignOfferForPartners,
    assignOfferForPartnersOfPartner,
    useTransactionOfferId,
}) => {
    const [availablePartners, setAvailablePartners] = useState([]);
    const [availablePartnersOfPartner, setAvailablePartnersOfPartner] = useState([]);
    const [unavailablePartners, setUnAvailablePartners] = useState([]);
    const [unavailablePartnersOfPartner, setUnvailablePartnersOfPartner] = useState([]);

    const [assigned, setAssigned] = useState([]);
    const [unassigned, setUnassigned] = useState([]);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

    const [initialPartnersData, setInitialPartnersData] = useState(null);
    const [initialPartnersOfPartnerData, setInitialPartnersOfPartnerData] = useState(null);

    const { data: availablePartnersData } = useQuery(GET_PARTNERS, {
        onCompleted: (data) => {
            if (!initialPartnersData) {
                setInitialPartnersData(data);
            }
        },
        onError: (error) => {
            console.error('Query error:', error);
        },
        fetchPolicy: 'cache-and-network',
    });

    const { data: availablePartnersOfPartnerData } = useQuery(GET_PARTNER_OF_PARTNERS_OF_USER, {
        onCompleted: (data) => {
            if (!initialPartnersOfPartnerData) {
                setInitialPartnersOfPartnerData(data);
            }
        },
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        // Use either the initial data if available or the current data from the queries
        const partnersData = initialPartnersData || availablePartnersData;
        const partnersOfPartnerData =
            initialPartnersOfPartnerData || availablePartnersOfPartnerData;

        if (idToAssign && partnersData && partnersOfPartnerData) {
            const processedAvailablePartners = (partnersData?.partners ?? [])
                .filter((ap) => !assignedPartners.some((apAssigned) => apAssigned.value === ap.id))
                .map(({ id, business, partnerOfPartner }) => ({
                    value: id,
                    label: business?.legalName,
                    type: 'partner',
                    businessId: business?.id,
                    linkedId: partnerOfPartner?.map((pp) => pp.business?.id).filter((id) => id),
                }));

            const processedUnAvailablePartners = (partnersData?.partners ?? [])
                .filter((ap) => assignedPartners.some((apAssigned) => apAssigned.value === ap.id))
                .map(({ id, business, partnerOfPartner }) => ({
                    value: id,
                    label: business?.legalName,
                    type: 'partner',
                    businessId: business?.id,
                    linkedId: partnerOfPartner?.map((pp) => pp.business?.id).filter((id) => id),
                }));

            const processedAvailablePartnersOfPartner = (
                partnersOfPartnerData?.partnerOfPartnersForUser ?? []
            )
                .filter(
                    (app) =>
                        !assignedPartnersOfPartners.some(
                            (appAssigned) => appAssigned.value === app.id
                        )
                )
                .map(({ id, business, partner }) => ({
                    value: id,
                    label: business?.legalName,
                    businessId: business?.id,
                    type: 'partnerOfPartner',
                    linkedId: [partner?.business?.id],
                }));

            const processedUnAvailablePartnersOfPartner = (
                partnersOfPartnerData?.partnerOfPartnersForUser ?? []
            )
                .filter((app) =>
                    assignedPartnersOfPartners.some((appAssigned) => appAssigned.value === app.id)
                )
                .map(({ id, business, partner }) => ({
                    value: id,
                    label: business?.legalName,
                    businessId: business?.id,
                    type: 'partnerOfPartner',
                    linkedId: [partner?.business?.id],
                }));

            setUnAvailablePartners(processedUnAvailablePartners);
            setUnvailablePartnersOfPartner(processedUnAvailablePartnersOfPartner);
            setAvailablePartners(processedAvailablePartners);
            setAvailablePartnersOfPartner(processedAvailablePartnersOfPartner);

            setAssigned([
                ...processedUnAvailablePartners,
                ...processedUnAvailablePartnersOfPartner,
            ]);
            setUnassigned([...processedAvailablePartners, ...processedAvailablePartnersOfPartner]);
        }
    }, [
        idToAssign,
        availablePartnersData,
        availablePartnersOfPartnerData,
        assignedPartners,
        assignedPartnersOfPartners,
        initialPartnersData,
        initialPartnersOfPartnerData,
    ]);

    const handleAssignPartners = async (assigned, unassigned) => {
        const currentAssignedPartners = assignedPartners.concat(assignedPartnersOfPartners);

        const newAssignments = assigned.filter(
            ({ value, type }) =>
                !currentAssignedPartners.some((p) => p.value === value && p.type === type)
        );

        const newUnassignments = unassigned.filter(({ value, type }) =>
            currentAssignedPartners.some((p) => p.value === value && p.type === type)
        );

        const partnerIdsToAssign = newAssignments
            .filter((p) => p.type === 'partner')
            .map((p) => p.value);
        const partnerOfPartnerIdsToAssign = newAssignments
            .filter((p) => p.type === 'partnerOfPartner')
            .map((p) => p.value);

        const partnerIdsToUnassign = newUnassignments
            .filter((p) => p.type === 'partner')
            .map((p) => p.value);
        const partnerOfPartnerIdsToUnassign = newUnassignments
            .filter((p) => p.type === 'partnerOfPartner')
            .map((p) => p.value);

        const variableKey = useTransactionOfferId ? 'transactionOfferId' : 'offerId';

        if (partnerIdsToAssign.length > 0) {
            await assignOfferForPartners({
                variables: {
                    partnerIds: partnerIdsToAssign,
                    [variableKey]: idToAssign,
                    type: PricingsAssignmentType.ASSIGN_OFFER,
                },
            });
        }
        if (partnerOfPartnerIdsToAssign.length > 0) {
            await assignOfferForPartnersOfPartner({
                variables: {
                    partnerOfPartnerIds: partnerOfPartnerIdsToAssign,
                    [variableKey]: idToAssign,
                    type: PricingsAssignmentType.ASSIGN_OFFER,
                },
            });
        }
        if (partnerIdsToUnassign.length > 0) {
            await assignOfferForPartners({
                variables: {
                    partnerIds: partnerIdsToUnassign,
                    [variableKey]: idToAssign,
                    type: PricingsAssignmentType.UNASSIGN_OFFER,
                },
            });
        }
        if (partnerOfPartnerIdsToUnassign.length > 0) {
            await assignOfferForPartnersOfPartner({
                variables: {
                    partnerOfPartnerIds: partnerOfPartnerIdsToUnassign,
                    [variableKey]: idToAssign,
                    type: PricingsAssignmentType.UNASSIGN_OFFER,
                },
            });
        }

        refetch();
    };

    const handleOpenAssignModal = (id) => {
        setIdToAssign(id);
        setIsAssignModalOpen(true);
    };

    const handleCloseAssignModal = () => {
        setIsAssignModalOpen(false);
        setIdToAssign(null);
        setAssigned([]);
        setUnassigned([]);
    };

    return {
        assigned,
        unassigned,
        handleAssignPartners,
        handleOpenAssignModal,
        handleCloseAssignModal,
        isAssignModalOpen,
    };
};
